import { useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../../hooks/auth/useAuth'
import { ROUTES } from '../../constants/routes'
import SocialAuthButton from '../../components/ui/SocialAuthButton'
import { authConfig, hasSocialAuth } from '../../config/auth'
import Logo from '../../components/ui/Logo'
import { AuthLayout } from '../../layouts/AuthLayout'
import { Turnstile } from '../../components/ui/Turnstile'
import { FormInput, FormButton } from '../../components/forms'
import { Alert, LegalFooter, Link as LinkUI } from '../../components/ui'
import { setLoginIntent } from '../../utils/loginIntent'

export const Register = () => {
  const { signUpWithEmail, signInWithProvider } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: location.state?.email || '',
    password: '',
    confirmPassword: '',
  })

  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState(null)
  const [fieldErrors, setFieldErrors] = useState({})
  const message = location.state?.message || ''

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))
    // Clear field error when user starts typing
    if (fieldErrors[name]) {
      setFieldErrors((prev) => ({
        ...prev,
        [name]: null,
      }))
    }
    // Validate password match in real-time
    if (name === 'confirmPassword' || (name === 'password' && formData.confirmPassword)) {
      const passwordToCheck = name === 'password' ? value : formData.password
      const confirmToCheck = name === 'confirmPassword' ? value : formData.confirmPassword
      if (confirmToCheck && passwordToCheck !== confirmToCheck) {
        setFieldErrors((prev) => ({
          ...prev,
          confirmPassword: 'Passwords do not match',
        }))
      } else {
        setFieldErrors((prev) => ({
          ...prev,
          confirmPassword: null,
        }))
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setLoading(true)

    // Trim all inputs
    const trimmedData = Object.keys(formData).reduce((acc, key) => {
      acc[key] = formData[key].trim()
      return acc
    }, {})

    // Validation
    if (
      !trimmedData.firstName ||
      !trimmedData.lastName ||
      !trimmedData.email ||
      !trimmedData.password ||
      !trimmedData.confirmPassword
    ) {
      setError('Please fill in all required fields')
      setLoading(false)
      return
    }

    // Check Turnstile token if enabled
    if (import.meta.env.VITE_TURNSTILE_ENABLED === 'true' && !turnstileToken) {
      setError('Please complete the security verification')
      setLoading(false)
      return
    }

    if (trimmedData.password !== trimmedData.confirmPassword) {
      setError('Passwords do not match')
      setLoading(false)
      return
    }

    if (trimmedData.password.length < 6) {
      setError('Password must be at least 6 characters long')
      setLoading(false)
      return
    }

    try {
      // Verify Turnstile token if enabled
      if (import.meta.env.VITE_TURNSTILE_ENABLED === 'true' && turnstileToken) {
        try {
          const verifyResponse = await fetch('/api/verify-turnstile', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ token: turnstileToken }),
          })

          const verifyResult = await verifyResponse.json()

          if (!verifyResult.success) {
            setError('Security verification failed. Please try again.')
            setLoading(false)
            return
          }
        } catch (error) {
          console.error('Turnstile verification error:', error)
          setError('Security verification failed. Please try again.')
          setLoading(false)
          return
        }
      }

      // Track user initiated registration
      setLoginIntent()

      // Sign up with Supabase
      const response = await signUpWithEmail(trimmedData.email, trimmedData.password, {
        data: {
          first_name: trimmedData.firstName,
          last_name: trimmedData.lastName,
        },
      })

      // Check if user was auto-signed in (happens when email confirmation is disabled)
      if (response?.session) {
        // User was auto-signed in, redirect to home
        // The AuthContext will show the "Welcome to Et Ai" toast
        navigate('/')
      } else {
        // Email confirmation is required - redirect to login with message
        window.location.href =
          '/login?message=Account created! Please check your email to confirm your account.'
      }
    } catch (error) {
      console.error('Registration error:', error)
      if (error.message?.includes('already registered')) {
        setError('This email is already registered. Please sign in instead.')
      } else if (error.message?.includes('Invalid email')) {
        setError('Please enter a valid email address.')
      } else {
        setError(error.message || 'An error occurred during registration')
      }
    } finally {
      setLoading(false)
    }
  }

  const handleSocialAuth = async (provider) => {
    setError(null)
    setLoading(true)

    try {
      setLoginIntent() // Track OAuth registration intent
      await signInWithProvider(provider)
      // The OAuth flow will redirect automatically
    } catch (error) {
      console.error('Social auth error:', error)
      setError(`Failed to sign up with ${provider}. Please try again.`)
      setLoading(false)
    }
  }

  return (
    <>
      {/* Fixed Logo Header with Glassmorphism */}
      <div className="fixed top-0 left-0 right-0 z-20 glassmorphism-header py-6 flex justify-center items-center">
        <Link to={ROUTES.HOME}>
          <Logo className="text-white mx-auto" />
        </Link>
      </div>

      <AuthLayout>
        <div className="w-full max-w-md mt-20">
          <h1 className="text-2xl font-bold text-white text-center mb-8">Create Account</h1>

          {message && <Alert type="info" message={message} className="mb-4" />}
          {error && <Alert type="error" message={error} className="mb-4" />}

          {hasSocialAuth() && (
            <div className="space-y-3 mb-6">
              {authConfig.google.enabled && (
                <SocialAuthButton provider="google" onClick={handleSocialAuth} />
              )}
              {authConfig.facebook.enabled && (
                <SocialAuthButton provider="facebook" onClick={handleSocialAuth} />
              )}
            </div>
          )}

          {authConfig.email.enabled && hasSocialAuth() && (
            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-dark-border"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-dark-primary text-gray-500">or register with email</span>
              </div>
            </div>
          )}

          {authConfig.email.enabled && (
            <form onSubmit={handleSubmit} className="space-y-6">
              <FormInput
                id="firstName"
                name="firstName"
                type="text"
                label="First Name"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Enter your first name"
                required
                disabled={loading}
                autoComplete="given-name"
                error={fieldErrors.firstName}
              />

              <FormInput
                id="lastName"
                name="lastName"
                type="text"
                label="Last Name"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Enter your last name"
                required
                disabled={loading}
                autoComplete="family-name"
                error={fieldErrors.lastName}
              />

              <FormInput
                id="email"
                name="email"
                type="email"
                label="Email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                required
                disabled={loading}
                autoComplete="email"
                error={fieldErrors.email}
              />

              <FormInput
                id="password"
                name="password"
                type="password"
                label="Password"
                value={formData.password}
                onChange={handleChange}
                placeholder="Create a password (min 6 characters)"
                required
                disabled={loading}
                autoComplete="new-password"
                error={fieldErrors.password}
                helperText={!fieldErrors.password ? 'Minimum 6 characters' : undefined}
              />

              <FormInput
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                label="Confirm Password"
                value={formData.confirmPassword}
                onChange={handleChange}
                placeholder="Confirm your password"
                required
                disabled={loading}
                autoComplete="new-password"
                error={fieldErrors.confirmPassword}
              />

              {/* Turnstile CAPTCHA */}
              <Turnstile
                onSuccess={(token) => setTurnstileToken(token)}
                onError={() => {
                  setError('Security verification failed. Please try again.')
                  setTurnstileToken(null)
                }}
                onExpire={() => {
                  setTurnstileToken(null)
                }}
              />

              <FormButton
                type="submit"
                variant="primary"
                loading={loading}
                disabled={
                  loading || (import.meta.env.VITE_TURNSTILE_ENABLED === 'true' && !turnstileToken)
                }
                fullWidth
              >
                Create Account
              </FormButton>
            </form>
          )}

          <div className="mt-8 text-center text-sm text-gray-400">
            Already have an account?{' '}
            <LinkUI to={ROUTES.LOGIN} variant="white" className="font-medium">
              Sign in
            </LinkUI>
          </div>

          <LegalFooter prefix="By creating an account, you agree to our" className="mt-4" />
        </div>
      </AuthLayout>
    </>
  )
}
